import React, { useEffect, useRef, useState } from "react";
import {
  RefreshCw,
  Timer,
  Flame,
  Bike,
  Truck,
  ChefHat,
  FileText,
  X,
} from "lucide-react";
import { BrazilDate } from "../../../utils/BrazilDate";
import { useInspecaoPedidoTempo } from "../../../hooks/useInspecaoPedido";
import { useInspecaoContext } from "../context/InspecaoContext";
import type {
  InspecaoPedidoTempoResponse,
  DescricaoItemPedido,
} from "../../../services/inspecaoService";

export const InspecaoPedidosSection: React.FC = () => {
  const {
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    selectedLojaId,
    pedidoDisplayIdInput,
    setPedidoDisplayIdInput,
    appliedPedidoDisplayId,
    setAppliedPedidoDisplayId,
  } = useInspecaoContext();

  const [pedidoVisibleCount, setPedidoVisibleCount] = useState(10);
  const [pedidoModal, setPedidoModal] =
    useState<InspecaoPedidoTempoResponse | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const formatDescricaoItem = (item: DescricaoItemPedido): string[] => {
    const lines: string[] = [];
    const name = item.name || item.item_original_name || "Item";
    lines.push(name);
    if (item.borda && item.borda.trim()) {
      lines.push(`Borda: ${item.borda}`);
    }
    if (item.sabores && item.sabores.length > 0) {
      lines.push(`Sabores: ${item.sabores.join(", ")}`);
    }
    if (item.observacao && String(item.observacao).trim()) {
      lines.push(`Obs: ${item.observacao}`);
    }
    return lines;
  };

  const {
    data: pedidoTempos,
    isLoading: isLoadingPedido,
    refetch: refetchPedido,
    isFetching: isFetchingPedido,
    error: pedidoError,
  } = useInspecaoPedidoTempo(
    appliedPedidoDisplayId
      ? {
          displayId: appliedPedidoDisplayId,
          lojaId:
            selectedLojaId === "all" ? undefined : (selectedLojaId as number),
          dataInicio,
          dataFim,
        }
      : undefined
  );

  useEffect(() => {
    if (pedidoTempos && pedidoTempos.length > 0) {
      setPedidoVisibleCount(Math.min(10, pedidoTempos.length));
    } else {
      setPedidoVisibleCount(0);
    }
  }, [pedidoTempos, appliedPedidoDisplayId]);

  useEffect(() => {
    if (!pedidoTempos || pedidoTempos.length === 0) return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && pedidoVisibleCount < pedidoTempos.length) {
          setPedidoVisibleCount((current) =>
            Math.min(current + 10, pedidoTempos.length)
          );
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [pedidoTempos, pedidoVisibleCount]);

  const handleBuscarPedido = () => {
    const numeric = Number.parseInt(pedidoDisplayIdInput, 10);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setAppliedPedidoDisplayId(null);
      return;
    }
    setAppliedPedidoDisplayId(numeric);
    refetchPedido();
  };

  return (
    <div className="mt-6 rounded-lg bg-white p-4 shadow">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <span className="block text-sm font-medium text-neutral-800">
            Tempo por pedido
          </span>
          <span className="block text-xs text-neutral-500">
            Informe o número do pedido (display_id) para ver quanto tempo ele
            ficou em cada etapa.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-neutral-500">De</span>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              aria-label="Data inicial"
              className="h-9 rounded-lg border border-neutral-300 px-2 text-sm text-neutral-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-neutral-500">Até</span>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              aria-label="Data final"
              className="h-9 rounded-lg border border-neutral-300 px-2 text-sm text-neutral-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          <input
            type="number"
            min={1}
            value={pedidoDisplayIdInput}
            onChange={(event) => setPedidoDisplayIdInput(event.target.value)}
            placeholder="Nº do pedido"
            className="h-9 w-32 rounded-lg border border-neutral-300 px-2 text-sm text-neutral-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
          <button
            type="button"
            onClick={handleBuscarPedido}
            disabled={isFetchingPedido}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-70"
          >
            <RefreshCw
              size={16}
              className={isFetchingPedido ? "animate-spin" : ""}
            />
            Buscar pedido
          </button>
        </div>
      </div>

      {isLoadingPedido ? (
        <div className="py-8 text-center text-neutral-500">
          Buscando informações do pedido...
        </div>
      ) : pedidoError ? (
        <div className="py-8 text-center text-sm text-red-600">
          {(pedidoError as Error).message}
        </div>
      ) : !pedidoTempos && !appliedPedidoDisplayId ? (
        <div className="py-8 text-center text-neutral-500">
          Informe um número de pedido e clique em &quot;Buscar pedido&quot;.
        </div>
      ) : !pedidoTempos || pedidoTempos.length === 0 ? (
        <div className="py-8 text-center text-neutral-500">
          Nenhum dado encontrado para o pedido informado.
        </div>
      ) : (
        <div className="space-y-4">
          {pedidoTempos.slice(0, pedidoVisibleCount).map((pedido) => (
            <div
              key={`${pedido.displayId}-${pedido.createdAt ?? "sem-data"}`}
              className="rounded-lg border border-neutral-200 bg-neutral-50/80 p-4"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-600">
                <span>
                  Pedido{" "}
                  <span className="font-semibold">#{pedido.displayId}</span>
                </span>
                <div className="flex items-center gap-2">
                  {pedido.createdAt && (
                    <span>
                      Criado em:{" "}
                      {BrazilDate.formatBR(
                        BrazilDate.fromISOString(pedido.createdAt),
                        true
                      )}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setPedidoModal(pedido)}
                    className="inline-flex items-center gap-1 rounded border border-neutral-300 bg-white px-2 py-1 text-xs font-medium text-neutral-700 transition hover:bg-neutral-50"
                  >
                    <FileText size={14} />
                    Ver Pedido
                  </button>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {[
                  {
                    label: "Aguardando Montagem",
                    value: pedido.tempos.aguardandoMontagem,
                    icon: <ChefHat size={16} />,
                  },
                  {
                    label: "Montagem",
                    value: pedido.tempos.montagem,
                    icon: <ChefHat size={16} />,
                  },
                  {
                    label: "Forno",
                    value: pedido.tempos.forno,
                    icon: <Flame size={16} />,
                  },
                  {
                    label: "Aguardando Entrega",
                    value: pedido.tempos.aguardandoEntrega,
                    icon: <Truck size={16} />,
                  },
                  {
                    label: "Entrega",
                    value: pedido.tempos.entrega,
                    icon: <Bike size={16} />,
                  },
                  {
                    label: "Total das etapas",
                    value: pedido.tempos.totalEtapas,
                    icon: <Timer size={16} />,
                    highlight: true,
                  },
                ].map((card) => (
                  <div
                    key={card.label}
                    className={`rounded-lg border p-3 ${
                      card.highlight
                        ? "border-primary bg-primary/5"
                        : "border-neutral-200 bg-white"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 text-xs font-medium ${
                        card.highlight ? "text-primary" : "text-neutral-600"
                      }`}
                    >
                      {card.icon}
                      {card.label}
                    </div>
                    <div
                      className={`mt-1 text-lg font-semibold tabular-nums ${
                        card.highlight ? "text-primary" : "text-neutral-900"
                      }`}
                    >
                      {card.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div ref={loadMoreRef} />
        </div>
      )}

      {pedidoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-descricao-title"
          onClick={() => setPedidoModal(null)}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-sm overflow-hidden rounded-lg bg-warning-light shadow-2xl ring-2 ring-warning/25 ring-offset-1 ring-offset-warning-light"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPedidoModal(null)}
              className="absolute right-2 top-2 z-10 rounded-full bg-amber-200/80 p-1.5 text-amber-900/80 transition hover:bg-amber-300/90"
              aria-label="Fechar"
            >
              <X size={18} />
            </button>

            <div className="font-mono text-sm text-amber-950/90">
              <div className="flex flex-col items-center border-b border-dashed border-amber-700/30 px-6 pb-4 pt-6">
                <img
                  src="/logo.png"
                  alt="Logo"
                  className="h-14 w-auto object-contain brightness-0"
                />
                <div className="mt-3 text-center text-xs font-semibold uppercase tracking-wider text-amber-800/90">
                  Pedido
                </div>
                <div className="mt-1 text-2xl font-bold tabular-nums text-amber-900">
                  #{pedidoModal.displayId}
                </div>
                {pedidoModal.createdAt && (
                  <div className="mt-2 text-xs text-amber-800/80">
                    {BrazilDate.formatBR(
                      BrazilDate.fromISOString(pedidoModal.createdAt),
                      true
                    )}
                  </div>
                )}
              </div>

              <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
                {pedidoModal.descricao &&
                Array.isArray(pedidoModal.descricao) &&
                pedidoModal.descricao.length > 0 ? (
                  <ul className="space-y-4">
                    {(pedidoModal.descricao as DescricaoItemPedido[]).map(
                      (item, index) => (
                        <li
                          key={index}
                          className="border-b border-dashed border-amber-700/20 pb-3 last:border-0 last:pb-0"
                        >
                          {formatDescricaoItem(item).map((line, i) => (
                            <div
                              key={i}
                              className={
                                i === 0
                                  ? "font-semibold text-amber-950"
                                  : "mt-0.5 pl-2 text-amber-900/85"
                              }
                            >
                              {line}
                            </div>
                          ))}
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <p className="text-center text-amber-800/70">
                    Nenhuma descrição disponível.
                  </p>
                )}
              </div>

              <div className="border-t border-dashed border-amber-700/30 px-6 py-3 text-center text-xs text-amber-800/70">
                ••••••••••••••••••••••••••••••
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
